import { Card, Row, Typography} from "antd";
import { useMemo, useRef } from "react";
import React from "react";
import FillLevelTag from "./Tags/FillLevelTag";
import AirConditionTag from "./Tags/AirConditionTag";
import DiagnosticViewTag from "./Tags/DiagnosticViewTag";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { convertFahrenheitToCelsius } from "src/utils/generalUtils";

const { Text } = Typography;

interface SideInfoBarProps{
  binDto: PremierPlusBinDTO | null | undefined;
  innerGamGrainDepth: number | null | undefined;
  outerGamGrainDepth: number | null | undefined;
  onClickShowFillLevel: () => void;
  onClickShowPlenumTemp: () => void;
  onClickShowDiagnosticView: () => void;
}
const SideInfoBar : React.FC<SideInfoBarProps> = ({ binDto, innerGamGrainDepth, outerGamGrainDepth, onClickShowFillLevel, onClickShowPlenumTemp, onClickShowDiagnosticView }) => {

  const logsRef = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  
  const useCelsius = useMemo ( () => {
    return binDto?.platformSettings?.useMetricSystem ?? false;
  }, [binDto]);

  const headspaceRh = useMemo(() => {
      if (binDto?.gaMs != null && binDto?.gaMs?.length > 0) {
        if (binDto?.gaMs?.length === 0) {
          return null;
        }

        const gams = binDto?.gaMs.filter(gam => gam.relativeHumidity != null) ?? [];
        
        const avgRh = gams.reduce( (sum, gam) => sum + gam.relativeHumidity!, 0 ) / gams.length;
        return avgRh
      }
    return null;

  }, [binDto]);
  
  const headspaceTemp = useMemo(() => {
    if (binDto?.gaMs != null && binDto?.gaMs?.length > 0) {
      if (binDto?.gaMs?.length === 0) {
        return null;
      }

      const gams = binDto?.gaMs.filter(gam => gam.temperatureF != null) ?? [];

      const avgTempF = gams.reduce( (sum, gam) => sum + gam.temperatureF!, 0 ) / gams.length;
      
      return useCelsius ? convertFahrenheitToCelsius(avgTempF) : avgTempF;
    }
    return null;
  }, [binDto]);


  const headspaceEmc = useMemo(() => {
    if (binDto?.gaMs != null && binDto?.gaMs?.length > 0) {
      if (binDto?.gaMs?.length === 0) {
        return null;
      }

      const gams = binDto?.gaMs.filter(gam => gam.emc != null) ?? [];

      const avgEmc = gams.reduce( (sum, gam) => sum + gam.emc!, 0 ) / gams.length;
      return avgEmc;
    }
    return null;
  }, [binDto]);
  
  const ambientTemp = useMemo(() => {
    return useCelsius ? convertFahrenheitToCelsius(binDto?.averageAmbientRhtSensor_TemperatureF) : binDto?.averageAmbientRhtSensor_TemperatureF ?? null;
  }, [binDto]);
  
  const plenumTemp = useMemo(() => {
    return useCelsius ? convertFahrenheitToCelsius(binDto?.averagePlenumRhtSensor_TemperatureF) : binDto?.averagePlenumRhtSensor_TemperatureF ?? null;
  }, [binDto]);
  
  const plenumRh = useMemo(() => {
    return binDto?.averagePlenumRhtSensor_RhPercent ?? null;
  }, [binDto]);
  
  const ambientRh = useMemo(() => {
    return binDto?.averageAmbientRhtSensor_RhPercent ?? null;
  }, [binDto]);
  
  const plenumEmc = useMemo(() => {
    return binDto?.averagePlenumRhtSensor_McPercent ?? null;
  }, [binDto]);
  
  const ambientEmc = useMemo(() => {
    return binDto?.averagePlenumRhtSensor_McPercent?? null;
  }, [binDto]);
  
  const totalVolumeBushels = useMemo (() => {
    return binDto?.grainHeightState_TotalVolumeBushels ?? null;
  }, [binDto]);

  const fillPercent = useMemo (() => {
    return binDto?.grainHeightState_PercentFull ?? null;
  }, [binDto]);

  return (
    <div style={{ display:'flex', flexDirection:'column', width:'100%', height:'100%' }}>
      <div style={{ paddingBottom:'8px'}}>
          {
            binDto?.gaMs != null && binDto?.gaMs?.length > 0 &&
            <AirConditionTag 
            title={'Headspace Air Conditions'} 
            useCelsius={useCelsius}
            temp={headspaceTemp} 
            rh={headspaceRh} 
            emc={headspaceEmc} 
            />
          }
          <AirConditionTag 
          title={'Ambient Air Conditions'} 
          useCelsius={useCelsius}
          temp={ambientTemp} 
          rh={ambientRh} 
          emc={ambientEmc}
          />
          <AirConditionTag 
          title={'Plenum Air Conditions'} 
          useCelsius={useCelsius}
          temp={plenumTemp} 
          rh={plenumRh} 
          emc={plenumEmc}
          onClick={onClickShowPlenumTemp}
          />
          <DiagnosticViewTag onClick={onClickShowDiagnosticView} />
         {
            binDto?.gaMs != null && binDto?.gaMs?.length > 0 &&
            <FillLevelTag 
            onClick={onClickShowFillLevel}
            grainHeightCenterFt={innerGamGrainDepth}
            grainHeightPerimeterFt={outerGamGrainDepth}
            estimatedBushels={totalVolumeBushels}
            fillLevelPercent={fillPercent}
            />
          }
      </div>
        
        <Row 
        style={{
          border: `1px solid ${'#d9d9d9'}`,
          backgroundColor: '#ffffff',
          borderRadius: '0px',
        }}
        justify={'center'}>
          <Text strong>System Logs</Text>
        </Row>
        <Card 
        ref={logsRef}
          styles={{
            body: {
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: 4,
              paddingRight: 4,
              height:'31vh',
            }
          }}
          style={{
            border: `1px solid ${'#d9d9d9'}`,
            backgroundColor: '#ffffff',
            width: '100%',
            height: '100%',
            borderRadius: '0px',
            overflow: 'scroll',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}>
            <div style={{ flex: 1 }}>
              Logs only available on the physical HMI
            </div>
        </Card>
        <div style={{padding:'4px'}}/>
    </div>
  );
};

export default SideInfoBar;
